// Colgate University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Colgate's Common Data Set 2025-2026 (CDS-PDF-2025-2026.pdf), linked as
//    "2025-2026 Common Data Set (PDF)" from the Office of Institutional
//    Analysis's Common Data Set page (the SOURCES url). C1 applicants, admits
//    and enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027; C21/C22 early plans; G1/G5 2026-2027 costs (the "not yet
//    available" box is unchecked). G1 lists food and housing at $18,446 for
//    first-years and $19,192 for other undergraduates; the first-year figure
//    is used.
//    Quirk, early decision counts left off the page: C21 gives 1,342
//    applications and 477 admits under the form's "For the Fall 2024 entering
//    class" label, and the 2024-2025 edition, under the same label, gave
//    2,447 and the same 477. With the year unclear and the admit count
//    repeated, no early decision rate is shown.
//  - Colgate Admission: Apply to Colgate (deadlines table, test-optional
//    line) and First-Year Applicants (binding terms, required and optional
//    materials, testing details, arts portfolio). Neither page carries a
//    year; they are the pages as posted for the application that opened in
//    August 2026 (the Apply page profiles the Class of 2030).
//  - Colgate 2026-2027 University Catalog (majors; see the majors file).
//  - College Scorecard, school id 190099 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Colgate Common Data Set 2025-2026',
        url: 'https://www.colgate.edu/about/offices-centers-institutes/office-institutional-analysis/common-data-set',
        read: '2026-10-01',
    },
    apply: {
        label: 'Colgate Admission, Apply to Colgate',
        url: 'https://www.colgate.edu/admission-aid/apply',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Colgate Admission, First-Year Applicants',
        url: 'https://www.colgate.edu/admission-aid/apply/first-year-applicants',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190099-Colgate-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'colgate-university',
    kbId: 'colgate_university',
    name: 'Colgate University',
    shortName: 'Colgate',
    location: 'Hamilton, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Colgate athletics identity guidelines); decoration only.
    branding: {
        primary: '#821019',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/colgate_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 17310,
        admitted: 3030,
        enrolled: 805,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 2975, admitted: 648 },
            { group: 'Out-of-state', applied: 7380, admitted: 2079 },
            { group: 'International', applied: 6955, admitted: 303 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Colgate\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Colgate has Early Decision I and II and no early action plan. Its Common Data Set prints its early decision counts under a "Fall 2024 entering class" label and repeats the prior edition\'s admit count, so no early decision rate is shown here.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Colgate as a whole, with no separate school to choose. One optional piece:',
        rows: [
            { school: 'Arts portfolio (optional)', requirement: 'You may send a SlideRoom portfolio in visual art and media, music, theater or dance ($12 fee, waived with a Common App fee waiver), due with your application. A Colgate faculty member assesses it. You do not need a portfolio or audition to declare an arts major.' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Late March', binding: false },
        ],
        note: 'Financial aid forms are due on the application date in each round. Mid-year grades are due February 15. The application fee is $60, and Colgate waives it for all first-year applicants during October.',
        source: 'apply',
        binding: 'Early Decision I and II are both binding: if admitted, you agree to withdraw all other applications and attend Colgate. You, a parent or guardian and your guidance counselor sign the Early Decision Agreement.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Sending SAT or ACT scores is optional, and students who do not send them are at no disadvantage. You say on the application whether you want scores considered, and you may self-report them. Colgate superscores both the SAT and the ACT; admitted students who had scores considered then send official scores.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73206 },
            { item: 'Required fees', amount: 440 },
            { item: 'Food and housing (on campus, first year)', amount: 18446 },
            { item: 'Books and supplies', amount: 1570 },
            { item: 'Transportation', amount: 800 },
            { item: 'Other expenses', amount: 1148 },
        ],
        total: 95610,
        note: 'For a first-year student living on campus. Food and housing is $19,192 for other undergraduates.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 29409,
            byIncome: [
                { income: '$0 to $30,000', amount: 8610 },
                { income: '$30,001 to $48,000', amount: 7748 },
                { income: '$48,001 to $75,000', amount: 15150 },
                { income: '$75,001 to $110,000', amount: 26968 },
                { income: 'Over $110,000', amount: 49725 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Colgate\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
